import { ref, watch, type Ref } from 'vue'

import { findLibraryItemById, PLACEHOLDER_IMAGE_SRC } from '@/lib/library/image-source'
import { resolveStorageDownloadUrl } from '@/lib/firebase/storage'
import { useLibraryStore } from '@/stores/library'
import { isLoaded } from '@/types/load-state'

/** Resolves a library item's `assetId` to a render-facing `src`, reactively; falls back to {@link PLACEHOLDER_IMAGE_SRC} while unresolved. */
export function useImageSrc(assetId: Ref<string | null | undefined>): Ref<string> {
  const library = useLibraryStore()
  const src = ref(PLACEHOLDER_IMAGE_SRC)
  let requestId = 0

  watch(
    [assetId, () => library.items],
    ([id, items]) => {
      const thisRequest = ++requestId

      if (!id || !isLoaded(items)) {
        src.value = PLACEHOLDER_IMAGE_SRC
        return
      }

      const item = findLibraryItemById(items.value, id)
      if (!item) {
        src.value = PLACEHOLDER_IMAGE_SRC
        return
      }

      resolveStorageDownloadUrl(item.storagePath)
        .then((url) => {
          if (requestId === thisRequest) src.value = url
        })
        .catch(() => {
          if (requestId === thisRequest) src.value = PLACEHOLDER_IMAGE_SRC
        })
    },
    { immediate: true },
  )

  return src
}
